import { API_BASE_URL } from '../../config/api';
import {
  getStoredRefreshToken,
  getStoredToken,
  getTelegramInitData,
  storeRefreshToken,
  storeToken,
} from '../../utils/platform';

export type AuthTokens = {
  access_token: string;
  refresh_token?: string;
};

export type SessionEvent =
  | { type: 'refreshed'; accessToken: string; refreshToken?: string }
  | { type: 'cleared' };

const AUTH_SKIP_REFRESH = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/refresh',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/reset-password',
  '/api/v1/telegram-auth/login',
  '/api/v1/telegram-auth/login-widget',
  '/api/v1/google-auth/login',
]);

type SessionListener = (event: SessionEvent) => void;
const listeners = new Set<SessionListener>();

let refreshInFlight: Promise<boolean> | null = null;

export function shouldSkipAuthRefresh(path: string): boolean {
  const pathname = path.split('?')[0];
  return AUTH_SKIP_REFRESH.has(pathname);
}

export function isExpiredAuthError(status: number, body: string): boolean {
  if (status === 401) return true;
  const text = body.toLowerCase();
  return text.includes('jwt expired') || text.includes('tokenexpirederror') || text.includes('session_expired');
}

export function subscribeSession(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(event: SessionEvent) {
  listeners.forEach((listener) => listener(event));
}

export function persistTokens(tokens: AuthTokens) {
  storeToken(tokens.access_token);
  if (tokens.refresh_token) storeRefreshToken(tokens.refresh_token);
}

export function clearPersistedSession() {
  storeToken(null);
  storeRefreshToken(null);
}

function isAccessTokenExpired(token: string, skewMs = 10_000): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' && json.exp * 1000 <= Date.now() + skewMs;
  } catch {
    return false;
  }
}

export function accessTokenNeedsRefresh(token: string | null): boolean {
  return Boolean(token && isAccessTokenExpired(token));
}

async function postJson<T>(path: string, body: unknown): Promise<T | null> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    cache: 'no-store',
  });
  if (!res.ok) return null;
  return res.json() as Promise<T>;
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getStoredRefreshToken();
  if (refreshToken) {
    const data = await postJson<AuthTokens>('/api/v1/auth/refresh', { refresh_token: refreshToken });
    if (data?.access_token) {
      persistTokens(data);
      emit({ type: 'refreshed', accessToken: data.access_token, refreshToken: data.refresh_token });
      return true;
    }
  }

  const initData = getTelegramInitData();
  if (initData) {
    const data = await postJson<AuthTokens>('/api/v1/telegram-auth/login', { initData });
    if (data?.access_token) {
      persistTokens(data);
      emit({ type: 'refreshed', accessToken: data.access_token, refreshToken: data.refresh_token });
      return true;
    }
  }

  return false;
}

/** Single-flight refresh — concurrent expired requests share one rotate/re-login. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export function logoutSession() {
  const token = currentAccessToken();
  // A login that landed while refresh was in flight must not be wiped.
  if (token && !accessTokenNeedsRefresh(token)) return;
  clearPersistedSession();
  emit({ type: 'cleared' });
}

export function currentAccessToken(): string | null {
  return getStoredToken();
}
